import React, { useMemo, useState } from 'react';
import { DownloadIcon, FileIcon, SearchIcon, Trash2Icon, UploadIcon } from 'lucide-react';
import { useHrPayroll } from '../../../contexts/HrPayrollContext';
import { hrDocumentCategories } from '../../../data/hrDefaults';
import { HrDocumentCategory } from '../../../types/hr';
import { employeeName, formatDate } from '../../../utils/hrFormat';
import { Button } from '../../../components/ui/Button';
import { EmployeeSelect } from './EmployeeSelect';
import { HrModal } from './HrModal';
const MAX_BYTES = 700 * 1024;
const cls = 'mt-1 block w-full rounded-md border border-slate-300 bg-white p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';

/** Contracts, IDs, certificates and other employment records, stored against the employee. */
export function HrDocuments({
  employeeId
}: {
  employeeId?: string;
}) {
  const {
    documents,
    employees,
    uploadDocument,
    deleteDocument
  } = useHrPayroll();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    employeeId: employeeId ?? '',
    category: 'Employment contract' as HrDocumentCategory,
    title: '',
    expiresOn: ''
  });
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return documents.filter(d => (!employeeId || d.employeeId === employeeId) && (!category || d.category === category)).filter(d => !q || d.title.toLowerCase().includes(q) || d.fileName.toLowerCase().includes(q) || employeeName(employees.find(e => e.id === d.employeeId)).toLowerCase().includes(q)).sort((a, b) => b.uploadedAt.localeCompare(a.uploadedAt));
  }, [documents, employees, employeeId, category, query]);
  const save = async () => {
    setError('');
    const targetId = employeeId ?? form.employeeId;
    if (!targetId) return setError('Select an employee.');
    if (!file) return setError('Choose a file to upload.');
    if (file.size > MAX_BYTES) return setError('Files must be 700 KB or smaller. Scan at a lower resolution or as PDF.');
    setSaving(true);
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    }).catch(() => '');
    if (!dataUrl) {
      setSaving(false);
      return setError('The file could not be read.');
    }
    const result = await uploadDocument({
      employeeId: targetId,
      category: form.category,
      title: form.title.trim() || file.name,
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream',
      size: file.size,
      dataUrl,
      expiresOn: form.expiresOn || undefined
    });
    setSaving(false);
    if (!result.ok) return setError(result.error ?? 'Upload failed.');
    setUploading(false);
    setFile(null);
    setForm(f => ({
      ...f,
      title: '',
      expiresOn: ''
    }));
  };
  const remove = async (id: string, title: string) => {
    if (!window.confirm(`Delete "${title}"? This is recorded in the audit log.`)) return;
    const result = await deleteDocument(id);
    if (!result.ok) setError(result.error ?? 'Could not delete.');
  };
  return <section className="space-y-4" aria-label="HR documents" data-id="element-886" data-mp="2wbv2ledpqwry">
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-id="element-887" data-mp="2rfnv3h5o5d7w">
        <div className="relative min-w-[220px] flex-1" data-id="element-888" data-mp="2me8b7sf89cr3">
          <label htmlFor={`doc-search-${employeeId ?? 'all'}`} className="sr-only" data-id="element-889" data-mp="2idobqi76lx1s">Search documents</label>
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" data-id="element-890" data-mp="2dfv7vvtcpf4o" />
          <input id={`doc-search-${employeeId ?? 'all'}`} type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by title, file or employee" className="w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm" data-id="element-891" data-mp="gm1uv9h07ij1" />
        </div>
        <label className="text-sm" data-id="element-892" data-mp="39g8r4ex2l85a"><span className="sr-only" data-id="element-893" data-mp="39gs199ghas8r">Category</span>
          <select value={category} onChange={e => setCategory(e.target.value)} className="rounded-md border border-slate-300 bg-white p-2 text-sm" data-id="element-894" data-mp="2vi211kyd4cg6"><option value="" data-id="element-895" data-mp="30faoo12a58ot">All categories</option>{hrDocumentCategories.map(c => <option key={c} data-id="element-896" data-mp="2lrh4s465vk0f">{c}</option>)}</select>
        </label>
        <Button onClick={() => {
        setError('');
        setUploading(true);
      }} data-id="element-897" data-mp="2lpw9gcc0vx1i"><UploadIcon className="mr-1.5 h-4 w-4" aria-hidden="true" data-id="element-898" data-mp="2qj7hggskmjv0" /> Upload document</Button>
      </div>
      {error && !uploading && <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800" data-id="element-899" data-mp="2coy6zyh8fxl1">{error}</p>}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-900" data-mp="22sv76z9usb19">
        {rows.length === 0 ? <p className="py-12 text-center text-sm text-slate-500" data-id="element-901" data-mp="27shg4winf549">No documents on file.</p> : <ul className="divide-y divide-slate-100" data-id="element-902" data-mp="1o5mm96qqsrc3">
            {rows.map(d => <li key={d.id} className="flex flex-wrap items-center gap-3 px-5 py-3" data-id="element-903" data-mp="1j7bd6y89ukux">
                <FileIcon className="h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-904" data-mp="1j9fo404l8k4p" />
                <div className="min-w-0 flex-1" data-id="element-905" data-mp="1y1zf62egr15s">
                  <p className="truncate font-medium text-slate-900" data-id="element-906" data-mp="1t47otctgvn3p">{d.title}</p>
                  <p className="text-xs text-slate-500" data-id="element-907" data-mp="274iewdmycxnt">{d.category}{!employeeId && ` · ${employeeName(employees.find(e => e.id === d.employeeId))}`} · {(d.size / 1024).toFixed(0)} KB · {formatDate(d.uploadedAt)} by {d.uploadedByName}{d.expiresOn ? ` · expires ${formatDate(d.expiresOn)}` : ''}</p>
                </div>
                <a href={d.dataUrl} download={d.fileName} className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50" data-id="element-908" data-mp="2bws2vo4ibnng"><DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-909" data-mp="2luppk76pxcdy" /> Download</a>
                <button type="button" onClick={() => remove(d.id, d.title)} className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600" aria-label={`Delete ${d.title}`} data-id="element-910" data-mp="2sxcy82idsajd"><Trash2Icon className="h-4 w-4" data-id="element-911" data-mp="2xu3i70xb3qa2" /></button>
              </li>)}
          </ul>}
      </div>

      {uploading && <HrModal title="Upload HR document" onClose={() => setUploading(false)} footer={<>{error && <p role="alert" className="mr-auto self-center text-sm text-red-700" data-id="element-913" data-mp="3bodxysrbsa3z">{error}</p>}<Button variant="outline" onClick={() => setUploading(false)} data-id="element-914" data-mp="3gmfgnckua7ex">Cancel</Button><Button onClick={save} isLoading={saving} data-id="element-915" data-mp="3gos4u7h6xe0f">Upload</Button></>} data-id="element-912" data-mp="37m99s9jnc5oq">
          <div className="space-y-4" data-id="element-916" data-mp="3lmblfe1d22vj">
            {!employeeId && <EmployeeSelect id="doc-emp" label="Employee" value={form.employeeId} onChange={v => setForm({
          ...form,
          employeeId: v
        })} currentOnly={false} data-id="element-917" data-mp="3qklglon353h5" />}
            <div className="grid gap-4 sm:grid-cols-2" data-id="element-918" data-mp="1r9lpkqrfmjli">
              <div data-id="element-919" data-mp="1w62g1rm3ta1r"><label htmlFor="doc-cat" className="block text-sm font-medium text-slate-700" data-id="element-920" data-mp="1w7vmp6wpvu9r">Category</label><select id="doc-cat" value={form.category} onChange={e => setForm({
              ...form,
              category: e.target.value as HrDocumentCategory
            })} className={cls} data-id="element-921" data-mp="dbzicrqc793q">{hrDocumentCategories.map(c => <option key={c} data-id="element-922" data-mp="4j61ipv8s1nr">{c}</option>)}</select></div>
              <div data-id="element-923" data-mp="4jnvtgycg5t1"><label htmlFor="doc-exp" className="block text-sm font-medium text-slate-700" data-id="element-924" data-mp="4jnxrmhwmfvo">Expiry date (optional)</label><input id="doc-exp" type="date" value={form.expiresOn} onChange={e => setForm({
              ...form,
              expiresOn: e.target.value
            })} className={cls} data-id="element-925" data-mp="3vnmc6s67oi3m" /></div>
            </div>
            <div data-id="element-926" data-mp="s9i7hv6xfobp"><label htmlFor="doc-title" className="block text-sm font-medium text-slate-700" data-id="element-927" data-mp="s9i9g0qhlyec">Title</label><input id="doc-title" value={form.title} onChange={e => setForm({
            ...form,
            title: e.target.value
          })} placeholder="e.g. Signed employment contract 2026" className={cls} data-id="element-928" data-mp="n8cbnohyzkxy" /></div>
            <div data-id="element-929" data-mp="380xotqyfnxfm"><label htmlFor="doc-file" className="block text-sm font-medium text-slate-700" data-id="element-930" data-mp="380xnnnmp5re1">File (PDF or image, max 700 KB)</label><input id="doc-file" type="file" accept=".pdf,image/*,.doc,.docx" onChange={e => setFile(e.target.files?.[0] ?? null)} className="mt-1 block w-full text-sm" data-id="element-931" data-mp="32zqh1rwaiy8y" /></div>
          </div>
        </HrModal>}
    </section>;
}